import React, { useImperativeHandle, useRef, forwardRef, useEffect } from "react";

export interface BuzzHandle {
  play: () => void;
  startRinging: () => void;
  stopRinging: () => void;
}

const Buzz = forwardRef<BuzzHandle>((_, ref) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRingingRef = useRef(false);

  const handleAudioPlay = () => {
    console.log("Buzz");
  };

  const handleAudioError = (err: unknown) => {
    console.warn("Buzzer error:", err);
  };

  const clearStopTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      clearStopTimer();
      audio?.pause();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    play: () => {
      const audio = audioRef.current;
      if (!audio || isRingingRef.current) return;

      // Restart from the beginning for a crisp single buzz.
      clearStopTimer();
      audio.loop = false;
      audio.pause();
      audio.currentTime = 0;

      audio.play().catch(handleAudioError);

      // Auto-stop after 1 second to avoid long playback overlap.
      timeoutRef.current = setTimeout(() => {
        audio.pause();
        audio.currentTime = 0;
      }, 1000);
    },

    startRinging: () => {
      const audio = audioRef.current;
      if (!audio) {
        console.warn("[Buzz] Audio element not found");
        return;
      }
      if (isRingingRef.current) {
        console.log("[Buzz] Already ringing, ignoring startRinging call");
        return;
      }

      clearStopTimer();
      audio.loop = true;
      audio.currentTime = 0;

      console.log("[Buzz] Attempting to start ringing...");

      // Try to play with error handling for autoplay policy
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            isRingingRef.current = true;
            console.log("[Buzz] ✅ Audio playing successfully");
          })
          .catch((err: any) => {
            console.error("[Buzz] ❌ Audio play error:", err?.name, err?.message);
            handleAudioError(err);
          });
      } else {
        // Older browsers that don't return a promise
        isRingingRef.current = true;
      }
    },

    stopRinging: () => {
      const audio = audioRef.current;
      isRingingRef.current = false;
      if (!audio) return;
      audio.loop = false;
      audio.pause();
      audio.currentTime = 0;
    },
  }));

  return (
    <audio
      ref={audioRef}
      src="/notification.mp3"
      preload="auto"
      onPlay={handleAudioPlay}
      onError={handleAudioError}
    />
  );
});

export default Buzz;